import { inject } from '@angular/core'
import { Routes } from '@angular/router'
import { DEFAULT_LANGUAGE, HOME_SLUG } from '@pai/commons'

import { CmsService } from 'app/core/cms/cms.service'
import { provideLinksFromResolvedRouteData } from 'app/core/cms/links.token'
import { RouteMetaData } from 'app/core/meta/route-meta-data.type'
import { childRouteSsrPreventionGuard, EMPTY_ROUTE_FOR_SSR_PREVENTION } from 'app/core/ssr-boundary/utils'
import { RouteDataKey } from 'app/static/route-data-key.enum'
import { RoutePath } from 'app/static/route-path.enum'
import {
  getPathFromRouteUrl,
  nestedPathMatcher,
  resolveBySlug,
  resolveWithRedirectToHomeWhenNull,
  resolveWithRedirectWhenNull,
} from 'app/utils/routing-utils'

import { pageSeoInfo, projectSeoInfo, publicationSeoInfo } from './_shared/seo-info.function'
import { SiteWrapperComponent } from './site-wrapper/site-wrapper.component'

export const routes: Routes = [
  {
    path: RoutePath.PREVIEW,
    canActivate: [childRouteSsrPreventionGuard],
    data: { [RouteDataKey.META]: { noIndex: true } satisfies RouteMetaData },
    children: [
      EMPTY_ROUTE_FOR_SSR_PREVENTION,

      // http://localhost:4200/preview/live/de/projects/the-slug
      {
        path: `${RoutePath.PREVIEW__LIVE}/:lang/:collection`,
        data: { [RouteDataKey.META]: { noIndex: true } satisfies RouteMetaData },
        resolve: {
          [RouteDataKey.LINKS]: () => inject(CmsService).links,
        },
        providers: [provideLinksFromResolvedRouteData()],
        children: [
          {
            path: '**',
            loadComponent: () => import('./live-preview/live-preview.component'),
          },
        ],
      },

      // http://localhost:4200/preview/draft/de/projects/the-slug#preview-token=asdfghjk
      {
        path: `${RoutePath.PREVIEW__DRAFT}/:lang`,
        data: { [RouteDataKey.META]: { noIndex: true } satisfies RouteMetaData },
        resolve: {
          [RouteDataKey.LINKS]: () => inject(CmsService).links,
        },
        providers: [provideLinksFromResolvedRouteData()],
        children: [
          {
            matcher: nestedPathMatcher(`${RoutePath.PAGES}/:slug`),
            resolve: {
              [RouteDataKey.SITE]: resolveWithRedirectWhenNull(
                resolveBySlug((slug) => inject(CmsService).getPage(slug, true)),
                (lang) => ['/', RoutePath.PREVIEW, RoutePath.PREVIEW__DRAFT, lang, RoutePath.__NOT_FOUND],
              ),
            },
            loadComponent: () => import('./site-page/page-site.component'),
          },
          {
            matcher: nestedPathMatcher(`${RoutePath.PROJECTS}/:slug`),
            resolve: {
              [RouteDataKey.SITE]: resolveWithRedirectWhenNull(
                resolveBySlug((slug) => inject(CmsService).getProject(slug, true)),
                (lang) => ['/', RoutePath.PREVIEW, RoutePath.PREVIEW__DRAFT, lang, RoutePath.__NOT_FOUND],
              ),
            },
            loadComponent: () => import('./site-project/project-site.component'),
          },
          {
            matcher: nestedPathMatcher(`${RoutePath.PUBLICATIONS}/:slug`),
            resolve: {
              [RouteDataKey.SITE]: resolveWithRedirectWhenNull(
                resolveBySlug((slug) => inject(CmsService).getPublication(slug, true)),
                (lang) => ['/', RoutePath.PREVIEW, RoutePath.PREVIEW__DRAFT, lang, RoutePath.__NOT_FOUND],
              ),
            },
            loadComponent: () => import('./site-publication/publication-site.component'),
          },
          {
            path: RoutePath.__NOT_FOUND,
            loadComponent: () => import('./error/error.component'),
            data: { title: 'Preview Not Found' },
          },
        ],
      },
    ],
  },

  {
    path: `:lang`,
    component: SiteWrapperComponent,
    resolve: {
      [RouteDataKey.LINKS]: () => inject(CmsService).links,
      [RouteDataKey.HEADER]: () => inject(CmsService).header,
      [RouteDataKey.FOOTER]: () => inject(CmsService).footer,
    },
    children: [
      {
        path: RoutePath.__SERVER_ERROR,
        data: {
          [RouteDataKey.META]: { noIndex: true } satisfies RouteMetaData,
          title: 'Server Error',
        },
        loadComponent: () => import('./error/error.component'),
      },
      { path: HOME_SLUG, pathMatch: 'full', redirectTo: '' },
      {
        matcher: nestedPathMatcher(`${RoutePath.PROJECTS}/:slug`),
        data: {
          [RouteDataKey.META]: {
            seoRetrievalFn: (s) => projectSeoInfo(s.data[RouteDataKey.SITE]),
          } satisfies RouteMetaData,
        },
        resolve: {
          [RouteDataKey.SITE]: resolveWithRedirectToHomeWhenNull(
            resolveBySlug((slug) => inject(CmsService).getProject(slug)),
          ),
        },
        loadComponent: () => import('./site-project/project-site.component'),
      },
      {
        matcher: nestedPathMatcher(`${RoutePath.PUBLICATIONS}/:slug`),
        data: {
          [RouteDataKey.META]: {
            seoRetrievalFn: (s) => publicationSeoInfo(s.data[RouteDataKey.SITE]),
          } satisfies RouteMetaData,
        },
        resolve: {
          [RouteDataKey.SITE]: resolveWithRedirectToHomeWhenNull(
            resolveBySlug((slug) => inject(CmsService).getPublication(slug)),
          ),
        },
        loadComponent: () => import('./site-publication/publication-site.component'),
      },
      {
        // might be 'a/b/c' or ''
        path: '**',
        data: {
          [RouteDataKey.META]: {
            seoRetrievalFn: (s) => pageSeoInfo(s.data[RouteDataKey.SITE]),
          } satisfies RouteMetaData,
        },
        resolve: {
          [RouteDataKey.SITE]: resolveWithRedirectToHomeWhenNull(({ url }) =>
            inject(CmsService).getPage(getPathFromRouteUrl(url)),
          ),
        },
        loadComponent: () => import('./site-page/page-site.component'),
      },
    ],
  },

  {
    path: '',
    pathMatch: 'full',
    redirectTo: DEFAULT_LANGUAGE,
  },

  // no wildcard route necessary, `:lang` path catches the rest
]
